import { useState, useMemo, useCallback } from "react";
import type { Deal } from "../../../../../../../models";
import { useGrowerAssignment } from "../shared";
import type { DealConfigurationProps } from "./DealConfiguration.types";

export const DEAL_PROPERTIES: { name: string; label: string; getValue: (d: Deal) => string }[] = [
	{ name: "season", label: "Season", getValue: (d) => d.season },
	{ name: "type", label: "Type", getValue: (d) => d.type },
	{ name: "density", label: "Density", getValue: (d) => `${d.density} acre` },
	{ name: "depth", label: "Depth", getValue: (d) => `${d.depth} in` },
	{ name: "testPackage", label: "Package", getValue: (d) => d.testPackage || "None" },
	{ name: "frequencyOfMicros", label: "Micros", getValue: (d) => `${d.frequencyOfMicros ?? 0}` },
	{ name: "labName", label: "Lab", getValue: (d) => d.labName || "" },
	{ name: "specialTag", label: "Tag", getValue: (d) => d.specialTag || "" },
];

const CHIP_COLORS = [
	"#E3F2FD",
	"#FFF3E0",
	"#E8F5E9",
	"#FCE4EC",
	"#F3E5F5",
	"#E0F7FA",
	"#FFF9C4",
	"#F1F8E9",
	"#FFEBEE",
	"#EDE7F6",
	"#E0F2F1",
	"#FBE9E7",
];

export type DealSortOption =
	| "default"
	| "density-asc"
	| "density-desc"
	| "depth-asc"
	| "depth-desc"
	| "growers-desc"
	| "growers-asc";

export function useDealConfigurationViewModel(props: DealConfigurationProps) {
	const { jobs, allDeals, dealAssignments, onAssignGrowerToDeal } = props;

	const shared = useGrowerAssignment({
		jobs,
		entities: allDeals,
		assignments: dealAssignments,
		onAssign: onAssignGrowerToDeal,
	});

	// Build a deterministic color map: "propertyName:value" -> color
	const propertyColorMap = useMemo(() => {
		const map: Record<string, string> = {};

		let globalColorIdx = 0;
		for (const prop of DEAL_PROPERTIES) {
			const values = allDeals.map(prop.getValue).filter(Boolean);
			const uniqueValues = [...new Set(values)].sort();
			if (uniqueValues.length <= 1) continue;
			const counts = new Map<string, number>();
			for (const v of values) counts.set(v, (counts.get(v) || 0) + 1);
			const mostCommon = uniqueValues.reduce((a, b) => (counts.get(a)! >= counts.get(b)! ? a : b));
			for (const value of uniqueValues) {
				if (value === mostCommon) continue;
				map[`${prop.name}:${value}`] = CHIP_COLORS[globalColorIdx++ % CHIP_COLORS.length];
			}
		}
		return map;
	}, [allDeals]);

	// Deal sort & filter state
	const [dealSortOption, setDealSortOption] = useState<DealSortOption>("default");
	const [dealFilters, setDealFilters] = useState<Record<string, Set<string>>>({});

	const setDealFilter = useCallback((propertyName: string, values: Set<string>) => {
		setDealFilters((prev) => ({ ...prev, [propertyName]: values }));
	}, []);

	const clearDealFilters = useCallback(() => {
		setDealFilters({});
	}, []);

	// Unique values per property for the filter popover
	const dealFilterOptions = useMemo((): Record<string, string[]> => {
		const result: Record<string, string[]> = {};
		for (const prop of DEAL_PROPERTIES) {
			const values = allDeals.map(prop.getValue).filter(Boolean);
			result[prop.name] = [...new Set(values)].sort();
		}
		return result;
	}, [allDeals]);

	// Apply filters then sort
	const filteredSortedDeals = useMemo(() => {
		let deals = allDeals.filter((deal) => {
			for (const prop of DEAL_PROPERTIES) {
				const filterSet = dealFilters[prop.name];
				if (filterSet && filterSet.size > 0) {
					const value = prop.getValue(deal);
					if (!filterSet.has(value)) return false;
				}
			}
			return true;
		});

		if (dealSortOption !== "default") {
			deals = [...deals].sort((a, b) => {
				switch (dealSortOption) {
					case "density-asc":
						return a.density - b.density;
					case "density-desc":
						return b.density - a.density;
					case "depth-asc":
						return a.depth - b.depth;
					case "depth-desc":
						return b.depth - a.depth;
					case "growers-desc": {
						const aCount = (shared.growersByEntity.get(a.id) || []).length;
						const bCount = (shared.growersByEntity.get(b.id) || []).length;
						return bCount - aCount;
					}
					case "growers-asc": {
						const aCount = (shared.growersByEntity.get(a.id) || []).length;
						const bCount = (shared.growersByEntity.get(b.id) || []).length;
						return aCount - bCount;
					}
					default:
						return 0;
				}
			});
		}

		return deals;
	}, [allDeals, dealFilters, dealSortOption, shared.growersByEntity]);

	const activeDealFilterCount = useMemo(
		() => Object.values(dealFilters).filter((s) => s.size > 0).length,
		[dealFilters],
	);

	return {
		...shared,
		propertyColorMap,
		dealSortOption,
		setDealSortOption,
		dealFilters,
		setDealFilter,
		clearDealFilters,
		dealFilterOptions,
		filteredSortedDeals,
		activeDealFilterCount,
		totalDealCount: allDeals.length,
	};
}
